import { useCallback, useEffect, useState } from 'react';

import reposicaoVolemicaPng from '@assets/reposicao_volemica.png';
import scqPng from '@assets/SCQ.png';
import { PageTitle } from '@components/PageTitle';
import { Button } from '@components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { Separator } from '@components/ui/separator';
import { useAuth } from '@contexts/auth';
import { EHidratacaoQueimadoType } from '@enums/HidratacaoQueimadoType';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import BigNumber from 'bignumber.js';
import { CircleHelp } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { NumberFormatBase, NumericFormat } from 'react-number-format';
import { Navigate } from 'react-router';
import { z } from 'zod';

import CalculateSQC from './CalculateSCQ';
import { useHidratacaoQueimado } from './context';
import { formSchema } from './formSchema';
import SCQCalculated from './SCQCalculated';

enum ETab {
  Calculate = 'CALCULAR',
  WithoutCalculate = 'SEM_CALCULO',
}

function BurntHydration() {
  const { user } = useAuth();
  const { age, resetSCQ, SCQTotal, setAge, setType, setWeight, type, weight } =
    useHidratacaoQueimado();
  const [tabEnabled, setTabEnabled] = useState<ETab>(ETab.Calculate);

  const [totalVolume, setTotalVolume] = useState<number>(0);
  const [infusion, setInfusion] = useState<number>(0);
  const [firstVelocity, setFirstVelocity] = useState<number>(0);
  const [secondVelocity, setSecondVelocity] = useState<number>(0);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues: {
      weight: weight.toFixed(1),
      age: String(age),
      type,
      percentage: SCQTotal.toFixed(1),
    },
  });

  const handleChangeWeight = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setWeight(valueParsed);
    },
    [setWeight],
  );

  const handleChangeAge = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setAge(valueParsed);
    },
    [setAge],
  );

  const handleChangeType = useCallback(
    (value: EHidratacaoQueimadoType): void => {
      setType(value);
    },
    [setType],
  );

  function onSubmit(): void {
    //
  }

  const handleChangeTab = useCallback(
    (tab: ETab): void => {
      setTabEnabled(tab);
      resetSCQ();
    },
    [resetSCQ],
  );

  const calculateTotalVolume = useCallback(() => {
    // FÓRMULA:
    // SE tipo É elétrico, então 4 x peso x total SCQ;
    // SE tipo É térmico e (idade MENOR que 14 OU peso MENOR OU IGUAL a 30), então 3 x peso x total SCQ;
    // SE idade MAIOR OU IGUAL a 14 E tipo É térmico, então 2 x peso x total SCQ;

    const SCQtotalParsed = new BigNumber(SCQTotal);
    const weightParsed = new BigNumber(weight);
    if (type === EHidratacaoQueimadoType.Electrical) {
      const result = new BigNumber(4)
        .multipliedBy(weightParsed)
        .multipliedBy(SCQtotalParsed);
      setTotalVolume(result.decimalPlaces(0).toNumber() || 0);
      return;
    }
    if (
      type === EHidratacaoQueimadoType.Thermal &&
      (age < 14 || weight <= 30)
    ) {
      const result = new BigNumber(3)
        .multipliedBy(weightParsed)
        .multipliedBy(SCQtotalParsed);
      setTotalVolume(result.decimalPlaces(0).toNumber() || 0);
      return;
    }
    if (type === EHidratacaoQueimadoType.Thermal && age >= 14) {
      const result = new BigNumber(2)
        .multipliedBy(weightParsed)
        .multipliedBy(SCQtotalParsed);
      setTotalVolume(result.decimalPlaces(0).toNumber() || 0);
    }
  }, [SCQTotal, age, type, weight]);

  useEffect(() => {
    calculateTotalVolume();
  }, [calculateTotalVolume]);

  const calculateInfusion = useCallback(() => {
    // FÓRMULA: Total volume / 2
    const totalVolumeParsed = new BigNumber(totalVolume);
    const result = totalVolumeParsed.dividedBy(2);
    setInfusion(result.decimalPlaces(0).toNumber() || 0);
  }, [totalVolume]);

  useEffect(() => {
    calculateInfusion();
  }, [calculateInfusion]);

  const calculateFirstVelocity = useCallback(() => {
    // FÓRMULA: infusão / 8
    const infusionParsed = new BigNumber(infusion);
    const result = infusionParsed.dividedBy(8);
    setFirstVelocity(result.decimalPlaces(0).toNumber() || 0);
  }, [infusion]);

  useEffect(() => {
    calculateFirstVelocity();
  }, [calculateFirstVelocity]);

  const calculateSecondVelocity = useCallback(() => {
    // FÓRMULA: infusão / 16
    const infusionParsed = new BigNumber(infusion);
    const result = infusionParsed.dividedBy(16);
    setSecondVelocity(result.decimalPlaces(0).toNumber() || 0);
  }, [infusion]);

  useEffect(() => {
    calculateSecondVelocity();
  }, [calculateSecondVelocity]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'weight' && value.weight) {
        handleChangeWeight(value.weight);
      }
      if (name === 'age' && value.age) {
        handleChangeAge(value.age);
      }
      if (name === 'type' && value.type) {
        handleChangeType(value.type);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    setWeight(70);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user?.accessGranted) {
    return <Navigate replace to="/pagamento" />;
  }

  return (
    <div>
      <PageTitle hasBackButton title="Hidratação Queimado (Parkland)" />
      <Form {...form}>
        <form
          className="mx-auto max-w-3xl"
          onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="weight"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <FormLabel>Peso (Kg)</FormLabel>
                  <FormControl>
                    <NumberFormatBase
                      className="[&_input]:w-26 [&_input]:text-center"
                      customInput={Input}
                      format={(value) => formatterDecimal(value, 1)}
                      getCaretBoundary={(formattedValue) => {
                        const boundaryAry = Array.from({
                          length: formattedValue.length + 1,
                        }).map(() => true);
                        boundaryAry[0] = false;
                        boundaryAry[1] = false;
                        boundaryAry[2] = false;
                        return boundaryAry;
                      }}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="age"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <FormLabel>Idade (anos)</FormLabel>
                  <FormControl>
                    <NumericFormat
                      className="[&_input]:w-26 [&_input]:text-center"
                      customInput={Input}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger onBlur={field.onBlur}>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {[
                        {
                          label: 'Térmico',
                          value: EHidratacaoQueimadoType.Thermal,
                        },
                        {
                          label: 'Elétrico',
                          value: EHidratacaoQueimadoType.Electrical,
                        },
                      ].map((item) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={item.value}
                          value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          <Separator className="my-4" />

          <div className="mb-4 flex flex-row items-center justify-center">
            <Label>Superfície Corporal Queimada (SCQ)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="cursor-pointer" size="icon" variant="ghost">
                  <CircleHelp className="text-foreground" strokeWidth={2} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full max-w-sm space-y-4">
                <h1 className="text-secondary text-center font-medium">SCQ</h1>
                <img alt="SCQ" src={scqPng} />
              </PopoverContent>
            </Popover>
          </div>

          <div className="mb-4 flex flex-row justify-center gap-4">
            <Button
              className={cn(
                'border-tertiary hover:text-background hover:bg-tertiary cursor-pointer',
                tabEnabled === ETab.Calculate && 'bg-tertiary text-background',
              )}
              onClick={() => {
                handleChangeTab(ETab.Calculate);
              }}
              type="button"
              variant="outline">
              Calcular a SCQ
            </Button>
            <Button
              className={cn(
                'border-tertiary hover:text-background hover:bg-tertiary cursor-pointer',
                tabEnabled === ETab.WithoutCalculate &&
                  'bg-tertiary text-background',
              )}
              onClick={() => {
                handleChangeTab(ETab.WithoutCalculate);
              }}
              type="button"
              variant="outline">
              Já tenho a SCQ
            </Button>
          </div>

          {tabEnabled === ETab.Calculate && <CalculateSQC />}
          {tabEnabled === ETab.WithoutCalculate && <SCQCalculated />}

          <Separator className="my-4" />

          <div className="mb-4 flex flex-row items-center justify-center">
            <Label>Reposição Volêmica</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="cursor-pointer" size="icon" variant="ghost">
                  <CircleHelp className="text-foreground" strokeWidth={2} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full max-w-sm space-y-4">
                <h1 className="text-secondary text-center font-medium">
                  Reposição volêmica
                </h1>
                <img alt="Reposição volêmica" src={reposicaoVolemicaPng} />
              </PopoverContent>
            </Popover>
          </div>

          <div className="mb-4 flex flex-col items-center gap-2">
            <div className="flex flex-row items-center gap-2">
              <Label>Volume total:</Label>
              <p>
                <span className="text-tertiary text-lg font-bold">
                  {totalVolume}
                </span>{' '}
              </p>
              <Label className="text-foreground text-sm font-light">
                mL de Ringer
              </Label>
            </div>

            <div className="flex flex-row items-center gap-2">
              <Label>Infundir:</Label>
              <p>
                <span className="text-tertiary text-lg font-bold">
                  {infusion}
                </span>{' '}
              </p>
              <Label className="text-foreground text-sm font-light">
                mL de nas primeiras 8h
              </Label>
            </div>

            <div className="flex flex-row items-center gap-2">
              <Label>Velocidade:</Label>
              <p>
                <span className="text-tertiary text-lg font-bold">
                  {firstVelocity}
                </span>{' '}
              </p>
              <Label className="text-foreground text-sm font-light">mL/h</Label>
            </div>

            <div className="flex flex-row items-center gap-2">
              <Label>Após, infundir:</Label>
              <p>
                <span className="text-tertiary text-lg font-bold">
                  {infusion}
                </span>{' '}
              </p>
              <Label className="text-foreground text-sm font-light">
                mL nas próximas 16h
              </Label>
            </div>

            <div className="flex flex-row items-center gap-2">
              <Label>Velocidade:</Label>
              <p>
                <span className="text-tertiary text-lg font-bold">
                  {secondVelocity}
                </span>{' '}
              </p>
              <Label className="text-foreground text-sm font-light">mL/h</Label>
            </div>
          </div>

          <p className="text-foreground mt-8 text-center text-xs font-light">
            Referência: American College of Surgeons Committee on Trauma.
            Advanced Trauma Life Support (ATLS), 10th ed, American College of
            Surgeons
          </p>
        </form>
      </Form>
    </div>
  );
}
export default BurntHydration;
